<script>
  import {Map, NavigationControl, ScaleControl, setWorkerUrl} from 'maplibre-gl';
  import 'maplibre-gl/dist/maplibre-gl.css';

  import { onMount, onDestroy } from "svelte";
  import StationLayers from "./StationLayers.svelte";
  import { useViewportFetching } from "./useViewPortFetching.js";
  import { useDrawingControls } from "./useDrawingControls.js";
  import { startCacheCleanup, stopCacheCleanup, DEFAULT_VIEW } from "./mapUtils.js";
  import MapboxDraw from "../draw-compat.ts";
  import DrawRectangle, { DrawStyles } from "mapbox-gl-draw-rectangle-restrict-area";

  import DragCircleMode from "./draw-circle/Dragcirclemode.js";
  import DirectMode from "./draw-circle/Directmodeoverride.js";
  import SimpleSelectMode from "./draw-circle/Simpleselectmodeoverride.js";

  import {
    mapDisplayStations,
    selectedStations,
    viewMode,
    error as storeError,
    mapInstance,
    drawInstance,
    viewportSystem,
    drawingSystem
  } from "../../services/station-service.ts";

  export let width = 70;

  let map;
  let mapContainer;
  let draw;
  let polygonButtonRef, circleButtonRef, trashButtonRef, drawControlsObserver;
  let viewportFetchingSystem = null;
  let resizeObserver = null;

  setWorkerUrl(new URL(`${process.env.HUGO_BASEURL}/js/maplibre-gl-worker.mjs`).toString());
  const apiKey = process.env.ESRI_API_KEY;
  const styleUrl = `https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles/arcgis/streets?language=local&worldview=unitedStatesOfAmerica&token=${apiKey}`;

  const viewportFetching = useViewportFetching();
  const drawingControls = useDrawingControls();

  function setActiveDrawButton(activeBtn) {
    [polygonButtonRef, circleButtonRef].forEach((btn) => {
      if (btn) btn.classList.remove("draw-btn--selected");
    });
    if (activeBtn) activeBtn.classList.add("draw-btn--selected");
  }

  function handleModeChange() {}

  function createPolygonButton() {
    const button = document.createElement("button");
    button.className = "mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_polygon";
    button.title = "Rectangle tool";
    button.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path fill="currentColor" d="m15 12.3v-4.6c.6-.3 1-1 1-1.7 0-1.1-.9-2-2-2-.7 0-1.4.4-1.7 1h-4.6c-.3-.6-1-1-1.7-1-1.1 0-2 .9-2 2 0 .7.4 1.4 1 1.7v4.6c-.6.3-1 1-1 1.7 0 1.1.9 2 2 2 .7 0 1.4-.4 1.7-1h4.6c.3.6 1 1 1.7 1 1.1 0 2-.9 2-2 0-.7-.4-1.4-1-1.7zm-8-.3v-4l1-1h4l1 1v4l-1 1h-4z"/>
    </svg>`;
    return button;
  }

  function createCircleButton() {
    const button = document.createElement("button");
    button.className = "mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_circle";
    button.title = "Circle tool";
    button.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" stroke-width="2"/>
    </svg>`;
    return button;
  }

  function setupButtonHandlers(polygonButton, circleButton) {
    polygonButtonRef = polygonButton;
    circleButtonRef = circleButton;

    polygonButton.onclick = () => {
      if (!drawingControls.hasActiveShape()) {
        draw.changeMode("draw_polygon");
        setActiveDrawButton(polygonButton);
      }
    };

    circleButton.onclick = () => {
      if (!drawingControls.hasActiveShape()) {
        draw.changeMode("drag_circle");
        setActiveDrawButton(circleButton);
      }
    };

    drawingControls.setButtonRefs(polygonButton, circleButton);
  }

  function setupDrawControls() {
    const modes = {
      ...MapboxDraw.modes,
      draw_polygon: DrawRectangle,
      drag_circle: DragCircleMode,
      direct_select: DirectMode,
      simple_select: SimpleSelectMode
    };

    draw = new MapboxDraw({
      userProperties: true,
      controls: { trash: true },
      displayControlsDefault: false,
      styles: [...DrawStyles],
      modes
    });

    map.addControl(draw);
    drawInstance.set(draw);

    drawControlsObserver = new MutationObserver(() => {
      const trashButton = mapContainer.querySelector(".mapbox-gl-draw_trash");

      if (trashButton) {
        drawControlsObserver.disconnect();

        const controlGroup = trashButton.parentElement;

        const polygonButton = createPolygonButton();
        const circleButton = createCircleButton();

        controlGroup.insertBefore(polygonButton, trashButton);
        controlGroup.insertBefore(circleButton, trashButton);

        setupButtonHandlers(polygonButton, circleButton);

        trashButtonRef = trashButton;
        trashButtonRef.onclick = (e) => {
          e.preventDefault();
          drawingControls.deleteAllShapes();
        };
      }
    });

    drawControlsObserver.observe(mapContainer, {
      childList: true,
      subtree: true
    });
  }

  function initMap() {
    if (!mapContainer) return;

    map = new Map({
      container: mapContainer,
      ...DEFAULT_VIEW,
      style: styleUrl,
    });

    mapInstance.set(map);
    map.addControl(new NavigationControl(), "top-right");
    map.addControl(new ScaleControl(), "bottom-right");
    setupDrawControls();

    map.on("load", () => {
      viewportFetchingSystem = viewportFetching.setup(map);
      viewportSystem.set(viewportFetchingSystem);
      drawingControls.setup(map, draw, {
        onRefresh: viewportFetchingSystem.refresh,
        onShapeDeleted: () => setActiveDrawButton(null)
      });
      drawingSystem.set({
        clearShape: drawingControls.clearShapeForCoordinateSearch,
        hasActiveShape: drawingControls.hasActiveShape
      });

      map.on("draw.modechange", handleModeChange);
    });
  }

  $: if ($viewMode === "viewport" && map && viewportFetchingSystem) {
    viewportFetching.enable();
    viewportFetchingSystem.refresh();
  } else if (map && viewportFetchingSystem) {
    viewportFetching.disable();
  }

  onMount(() => {
    initMap();
    startCacheCleanup();

    let resizeTimeout;
    resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (map) map.resize();
      }, 100);
    });
    if (mapContainer) resizeObserver.observe(mapContainer);
  });

  onDestroy(() => {
    if (resizeObserver) resizeObserver.disconnect();
    if (map) map.off("draw.modechange", handleModeChange);
    stopCacheCleanup();
    viewportFetching.cleanup();
    drawingControls.cleanup();
    drawingSystem.set(null);
    if (map) map.remove();
  });
</script>

<div class="map-container" style="--map-width: {width}%">
  <div class="map-header">
    <div class="d-flex justify-content-between align-items-center">
      <h2>Station Explorer</h2>
      <div class="map-stats d-flex align-items-center">
        <span class="badge bg-primary me-2">
          {$mapDisplayStations.length} Stations on Map
        </span>
        <span class="badge bg-success">{$selectedStations.length} Selected</span>
      </div>
    </div>
  </div>
  <div class="map-content">
    <div id="map" bind:this={mapContainer}></div>
    {#if $storeError}
      <div class="fetch-error alert alert-warning">{$storeError}</div>
    {/if}
    <StationLayers {map} displayStations={$mapDisplayStations} />
  </div>
</div>

<style>
  .map-container {
    width: var(--map-width, 70%);
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .map-header {
    background-color: #162e51;
    color: white;
    padding: 1rem;
    flex-shrink: 0;
  }

  .map-content {
    flex: 1;
    position: relative;
  }

  #map {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  .fetch-error {
    position: absolute;
    top: 10px;
    left: 10px;
    right: 10px;
    z-index: 10;
    padding: 0.5rem 1rem;
  }

  :global(.map-container .mapbox-gl-draw_ctrl-draw-btn.mapbox-gl-draw_polygon),
  :global(.map-container .mapbox-gl-draw_ctrl-draw-btn.mapbox-gl-draw_circle) {
    color: #404040;
    background-image: none;
  }

  :global(.map-container .mapbox-gl-draw_ctrl-draw-btn.mapbox-gl-draw_polygon.draw-btn--selected),
  :global(.map-container .mapbox-gl-draw_ctrl-draw-btn.mapbox-gl-draw_circle.draw-btn--selected) {
    background-color: #007ec6;
    color: #fff;
  }

  :global(.map-container .badge.bg-success) {
    background-color: #c9520f !important;
  }

  :global(.maplibregl-map.mouse-add .maplibregl-canvas-container.maplibregl-interactive),
  :global(.maplibregl-map.mouse-add .maplibregl-canvas-container.maplibregl-interactive:active) {
    cursor: crosshair;
  }

  @media (max-width: 767px) {
    .map-container {
      width: 100%;
      height: auto;
    }

    .map-content {
      flex: 0 0 auto;
      height: 55vh;
      min-height: 300px;
    }

    .map-header {
      padding: 0.75rem;
    }

    .map-header h2 {
      font-size: 1.25rem;
    }

    .map-header > .d-flex {
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    :global(.map-container .maplibregl-ctrl-group > button) {
      width: 40px;
      height: 40px;
    }
  }
</style>